import { h, Component } from 'preact';
import { createPortal } from 'preact/compat';
import { Text } from 'preact-i18n';

import { shareTargets, SUPPORTED_TYPES, shareUrlFor } from './shareTargets';

const POPOVER_WIDTH = 240;
const POPOVER_GAP = 4;
const VIEWPORT_MARGIN = 8;
// Drawers stack at 1000000 + drawer * 10 (components/drawer/index.js, levels 1-9),
// so the portaled popover must sit above the topmost drawer to be visible.
const POPOVER_Z_INDEX = 1100000;

class ShareButton extends Component {
    constructor(props) {
        super(props);
        this.state = {
            popoverOpen: false,
            embedMode: false,
            copiedKey: null,
            triggerRect: null,
        };
        this.popoverRef = null;
        this.triggerRef = null;
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.popoverOpen && !prevState.popoverOpen) {
            document.addEventListener('click', this.handleOutsideClick, true);
            document.addEventListener('touchstart', this.handleOutsideClick, true);
            document.addEventListener('keydown', this.handleKeyDown, true);
            window.addEventListener('resize', this.handleReposition);
            window.addEventListener('scroll', this.handleReposition, true);
        } else if (!this.state.popoverOpen && prevState.popoverOpen) {
            document.removeEventListener('click', this.handleOutsideClick, true);
            document.removeEventListener('touchstart', this.handleOutsideClick, true);
            document.removeEventListener('keydown', this.handleKeyDown, true);
            window.removeEventListener('resize', this.handleReposition);
            window.removeEventListener('scroll', this.handleReposition, true);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleOutsideClick, true);
        document.removeEventListener('touchstart', this.handleOutsideClick, true);
        document.removeEventListener('keydown', this.handleKeyDown, true);
        window.removeEventListener('resize', this.handleReposition);
        window.removeEventListener('scroll', this.handleReposition, true);
    }

    handleOutsideClick = (e) => {
        if (this.popoverRef && !this.popoverRef.contains(e.target)
            && this.triggerRef && !this.triggerRef.contains(e.target)) {
            this.closePopover();
        }
    };

    handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            this.closePopover();
        }
    };

    handleReposition = () => {
        if (this.triggerRef) {
            this.setState({ triggerRect: this.triggerRef.getBoundingClientRect() });
        }
    };

    closePopover = () => {
        this.setState({ popoverOpen: false, embedMode: false, copiedKey: null, triggerRect: null });
    };

    buildShareUrl = () => {
        const { type, uuidv4 } = this.props;
        return shareUrlFor(type, uuidv4);
    };

    buildEmbedSnippet = () => {
        const url = this.buildShareUrl();
        const { title, summary, image } = this.props;
        const safeTitle = (title || '').replace(/</g, '&lt;');
        const safeSummary = (summary || '').replace(/</g, '&lt;');
        const host = window.location.hostname;

        const imageLine = image
            ? `\n  <img src="${image}" alt="" style="width:100%;height:auto;display:block">`
            : '';

        return `<a href="${url}"
   target="_blank" rel="noopener"
   style="display:inline-block;max-width:480px;border:1px solid #ddd;border-radius:10px;text-decoration:none;color:inherit;font-family:sans-serif;overflow:hidden">${imageLine}
  <div style="padding:12px">
    <div style="font-weight:600;margin-bottom:4px">${safeTitle}</div>
    <div style="font-size:0.9em;color:#666">${safeSummary}</div>
    <div style="font-size:0.8em;color:#999;margin-top:8px">${host}</div>
  </div>
</a>`;
    };

    copyText = (text, key) => {
        const onSuccess = () => {
            this.setState({ copiedKey: key });
            setTimeout(() => {
                if (this.state.copiedKey === key) this.setState({ copiedKey: null });
            }, 1500);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(onSuccess, (err) => {
                console.error('Clipboard write failed', err);
            });
            return;
        }
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            onSuccess();
        } catch (err) {
            console.error('execCommand copy failed', err);
        }
        document.body.removeChild(textArea);
    };

    handleTriggerClick = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { title, summary } = this.props;
        const url = this.buildShareUrl();

        if (navigator.share) {
            try {
                await navigator.share({
                    title: title || '',
                    text: summary || '',
                    url,
                });
            } catch (err) {
                // User cancelled — silent.
            }
            return;
        }

        const triggerRect = this.triggerRef ? this.triggerRef.getBoundingClientRect() : null;
        this.setState({ popoverOpen: true, embedMode: false, triggerRect });
    };

    computePopoverStyle = () => {
        const { triggerRect } = this.state;
        const { alignRight = false } = this.props;
        if (!triggerRect) return 'display: none;';

        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Decide direction: prefer below, but flip if there's no room below AND more room above.
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        const preferAbove = spaceBelow < 280 && spaceAbove > spaceBelow;

        const top = preferAbove
            ? Math.max(VIEWPORT_MARGIN, triggerRect.top - POPOVER_GAP)
            : triggerRect.bottom + POPOVER_GAP;

        let left = alignRight
            ? triggerRect.right - POPOVER_WIDTH
            : triggerRect.left;
        left = Math.max(VIEWPORT_MARGIN, Math.min(left, viewportWidth - POPOVER_WIDTH - VIEWPORT_MARGIN));

        const transform = preferAbove ? 'transform: translateY(-100%);' : '';
        const maxHeight = preferAbove ? spaceAbove - VIEWPORT_MARGIN * 2 : spaceBelow - VIEWPORT_MARGIN;

        return `position: fixed; top: ${top}px; left: ${left}px; width: ${POPOVER_WIDTH}px; max-height: ${maxHeight}px; overflow-y: auto; z-index: ${POPOVER_Z_INDEX}; ${transform}`;
    };

    renderPopoverContent = () => {
        const { title, summary } = this.props;
        const { copiedKey, embedMode } = this.state;
        const url = this.buildShareUrl();

        if (embedMode) {
            return (
                <div class='list-group-item'>
                    <div class='d-flex justify-content-between align-items-center mb-2'>
                        <strong><Text id='share.embed'>Embed</Text></strong>
                        <button
                            type='button'
                            class='btn btn-link p-0 text-muted'
                            onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); this.setState({ embedMode: false }); }}
                        >
                            <i class='fa-regular fa-arrow-left' />
                        </button>
                    </div>
                    <div class='small text-body-secondary mb-2'>
                        <Text id='share.embedHelp'>Paste this on your website</Text>
                    </div>
                    <textarea
                        class='form-control font-monospace'
                        rows={6}
                        readOnly
                        onClick={(ev) => ev.target.select()}
                        value={this.buildEmbedSnippet()}
                    />
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-secondary mt-2 w-100'
                        onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); this.copyText(this.buildEmbedSnippet(), 'embed'); }}
                    >
                        {copiedKey === 'embed'
                            ? <Text id='share.copied'>Copied!</Text>
                            : <Text id='share.copyEmbed'>Copy embed code</Text>}
                    </button>
                </div>
            );
        }

        return (
            <>
                <div class='list-group-item text-body-secondary small'>
                    <Text id='share.shareVia'>Share via</Text>
                </div>
                {shareTargets.map(target => (
                    <a
                        key={target.key}
                        href={target.build({ url, title, summary })}
                        target='_blank'
                        rel='noopener noreferrer'
                        class='list-group-item list-group-item-action d-flex align-items-center'
                        onClick={(ev) => { ev.stopPropagation(); this.closePopover(); }}
                    >
                        <i class={`${target.icon} me-2`} style={`color: ${target.color}; width: 1.2em;`} />
                        <span>{target.label}</span>
                    </a>
                ))}
                <button
                    type='button'
                    class='list-group-item list-group-item-action d-flex align-items-center'
                    onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); this.copyText(url, 'link'); }}
                >
                    <i class='fa-regular fa-link me-2' style='color: #6c757d; width: 1.2em;' />
                    <span>
                        {copiedKey === 'link'
                            ? <Text id='share.copied'>Copied!</Text>
                            : <Text id='share.copyLink'>Copy link</Text>}
                    </span>
                </button>
                <button
                    type='button'
                    class='list-group-item list-group-item-action d-flex align-items-center'
                    onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); this.setState({ embedMode: true }); }}
                >
                    <i class='fa-regular fa-code me-2' style='color: #6c757d; width: 1.2em;' />
                    <span><Text id='share.embed'>Embed</Text></span>
                </button>
            </>
        );
    };

    render() {
        const { type, uuidv4 } = this.props;
        const { popoverOpen } = this.state;

        if (!uuidv4 || !SUPPORTED_TYPES.includes(type)) return null;

        const popover = popoverOpen ? (
            <div
                ref={el => this.popoverRef = el}
                class='list-group shadow'
                style={this.computePopoverStyle()}
            >
                {this.renderPopoverContent()}
            </div>
        ) : null;

        return (
            <small class='ms-4 likes' ref={el => this.triggerRef = el}>
                <button
                    class='btn btn-link p-0 text-muted'
                    onClick={this.handleTriggerClick}
                >
                    <i class='fa-regular fa-share' /> <Text id='share.share'>Share</Text>
                </button>
                {popover && createPortal(popover, document.body)}
            </small>
        );
    }
}

export default ShareButton;
